import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  MessageSquare,
  BarChart3,
  AlertCircle,
  Settings
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ModernCard } from '../Modern';

import ContactsTab from './ContactsTab';
import CampaignTab from './CampaignTab';
import CampaignProgress from './CampaignProgress';
import AnalyticsTab from './AnalyticsTab';
import SettingsTab from './SettingsTab';
import apiService from '../../services/api';
import campaignCache from '../../utils/campaignCache';

const CRMDashboard = () => {
  // Obter estado do tema
  const { isDarkMode } = useTheme();
  
  // Estados principais
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para campanhas ativas
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [checkingActiveCampaign, setCheckingActiveCampaign] = useState(true);

  /**
   * Verificar se há campanha ativa ao carregar o componente
   * Esta função é executada quando o dashboard é montado para verificar
   * se existe alguma campanha em andamento que precisa ser exibida
   */
  useEffect(() => {
    const verificarCampanhaAtiva = async () => {
      try {
        setCheckingActiveCampaign(true);
        
        // Primeiro, verificar se há campanha salva no cache
        // Isso permite recuperar campanhas ativas após recarregar a página
        const campanhaAnalisada = campaignCache.load();
        if (campanhaAnalisada) {
            
            // MELHORIA: Sempre verificar o status na API, mas preservar dados do cache se mais recentes
            const resposta = await apiService.getCampaignStatus(campanhaAnalisada.id);
            if (resposta.success && resposta.data && 
                (resposta.data.status === 'active' || resposta.data.status === 'paused')) {
              
              // Mesclar dados da API com dados do cache, preservando o maior progresso
              const campanhaAtualizada = campaignCache.merge(resposta.data, campanhaAnalisada);
              
              setActiveCampaign(campanhaAtualizada);
              setShowProgress(true);
              
              // Atualizar cache com dados mesclados
              campaignCache.save(campanhaAtualizada);
              return;
            } else {
              // Campanha não está mais ativa, limpar cache
              campaignCache.clear();
            }
        }

        // Se não há campanha salva ou ela não está mais ativa, verificar diretamente na API
        // Isso garante que campanhas iniciadas em outras sessões sejam detectadas
        const respostaAtiva = await apiService.getActiveCampaign();
        if (respostaAtiva.success && respostaAtiva.data) {
          setActiveCampaign(respostaAtiva.data);
          setShowProgress(true);
          
          // Salvar no cache para recuperação em futuras sessões
          campaignCache.save(respostaAtiva.data);
        }
      } catch (erro) {
        // Em caso de erro na verificação, limpar dados possivelmente corrompidos
        campaignCache.clear();
      } finally {
        setCheckingActiveCampaign(false);
      }
    };

    verificarCampanhaAtiva();
  }, []);

  // Limpar mensagens quando mudarem
  useEffect(() => {
    if (error || success) {
      const timeout = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [error, success]);

  // Função para mostrar mensagem de erro
  const showError = useCallback((message) => {
    setError(message);
  }, []);

  // Função para mostrar mensagem de sucesso
  const showSuccess = useCallback((message) => {
    setSuccess(message);
  }, []);

  // Função para iniciar campanha e mostrar progresso
  const handleCampaignStart = (campaignData) => {
    setActiveCampaign(campaignData);
    setShowProgress(true);
    // Salvar no cache
    campaignCache.save(campaignData);
  };

  // Função para finalizar campanha
  const handleCampaignEnd = () => {
    setActiveCampaign(null);
    setShowProgress(false);
    // Remover do cache
    campaignCache.clear();
  };

  // Mostrar loading enquanto verifica campanha ativa
  if (checkingActiveCampaign) {
    return (
      <div className="w-full">
        <div className="text-center mb-12">
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Verificando campanhas ativas...
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className={`animate-spin rounded-full h-12 w-12 border-4 ${isDarkMode ? 'border-purple-800 border-t-purple-400' : 'border-purple-200 border-t-purple-600'}`}></div>
          <span className={`ml-4 text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando...</span>
        </div>
      </div>
    );
  }

  return (
      <div className="w-full">
        <div className="space-y-8">
          {/* Removido o header que agora está no componente pai */}

          {/* Mensagens de erro/sucesso */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <p className="text-sm font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Se há campanha ativa, mostrar apenas o progresso */}
          {showProgress && activeCampaign ? (
            <CampaignProgress
              campaign={activeCampaign}
              onCampaignEnd={handleCampaignEnd}
              onError={showError}
              onSuccess={showSuccess}
            />
          ) : (
            <>
              {/* Tabs - só mostrar se não há campanha ativa */}
              <div className="mb-8">
                <ModernCard themeColor="purple" className="p-2">
                  <nav className="flex space-x-2">
                    <button
                      onClick={() => setActiveTab('whatsapp')}
                      className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        activeTab === 'whatsapp'
                          ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg'
                          : isDarkMode 
                            ? 'text-gray-300 hover:text-purple-400 hover:bg-gray-700/50' 
                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      CRM WhatsApp
                    </button>
                    <button
                      onClick={() => setActiveTab('contatos')}
                      className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        activeTab === 'contatos'
                          ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg'
                          : isDarkMode 
                            ? 'text-gray-300 hover:text-purple-400 hover:bg-gray-700/50' 
                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Contatos
                    </button>
                    <button
                      onClick={() => setActiveTab('analytics')}
                      className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        activeTab === 'analytics'
                          ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg'
                          : isDarkMode 
                            ? 'text-gray-300 hover:text-purple-400 hover:bg-gray-700/50' 
                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Analytics
                    </button>
                    <button
                      onClick={() => setActiveTab('configuracoes')}
                      className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        activeTab === 'configuracoes'
                          ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg'
                          : isDarkMode 
                            ? 'text-gray-300 hover:text-purple-400 hover:bg-gray-700/50' 
                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Configurações
                    </button>
                  </nav>
                </ModernCard>
              </div>

              {/* Conteúdo das abas */}
              <ModernCard themeColor="purple" gradient glow>
                  {activeTab === 'whatsapp' && (
                    <CampaignTab
                      onCampaignStart={handleCampaignStart}
                      onError={showError}
                      onSuccess={showSuccess}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  )}

                  {activeTab === 'contatos' && (
                    <ContactsTab
                      onError={showError}
                      onSuccess={showSuccess}
                    />
                  )}

                  {activeTab === 'analytics' && (
                    <AnalyticsTab
                      onError={showError}
                      onSuccess={showSuccess}
                    />
                  )}

                  {activeTab === 'configuracoes' && (
                    <SettingsTab />
                  )}
                </ModernCard>
            </>
          )}
        </div>
      </div>
  );
};

export default CRMDashboard;